/**
 * Route-level permission resolution, extracted from AppRoutes so it can be tested against the
 * REAL implementation.
 *
 * It used to live inside AppRoutes.tsx as module-private functions, and hasModulePermission.test.ts
 * carried a hand-copied duplicate with a note explaining they "cannot be imported directly". A
 * test that re-implements what it guards proves only that the copy is self-consistent -- it would
 * not have caught the depth bug below.
 */
// ============================================================================
// MODULE PERMISSION TYPES
// ============================================================================

export interface ModulePermission {
  key: string
  name: string
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  submodules?: ModulePermission[]
}

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

/**
 * Module key aliases for role-specific permission mappings
 * Some routes use a generic moduleKey (like 'users_roles') but company/agency admins
 * have role-specific keys (like 'company_users_roles' or 'agency_users_roles')
 */
export const MODULE_KEY_ALIASES: Record<string, string[]> = {
  users_roles: ['company_users_roles', 'agency_users_roles', 'agency-users'],
  positions: ['company_positions'],
  company_positions: ['positions'],
  // manage_agencies can be accessed by users with manage_profile permission
  // (for managing their own agency profile via AgencyProfileRedirect)
  manage_agencies: ['manage_profile'],
  // Agency category permissions from BE use "agency-*" prefixed keys
  // Map standard route config keys to their agency-prefixed equivalents
  dashboard: ['agency-company-management'], // Dashboard is available to all agency users; any agency key grants access
  manage_companies: ['agency-company-management'],
  manage_orders: ['agency-job-orders', 'agency-assignments'],
  candidates: ['agency-candidates'],
  pre_fill: ['agency-job-orders'],
  // manage_profile is the route-level key for agency "Manage Profile" page
  manage_profile: ['agency-billing', 'agency-reports'],
}

/**
 * Modules a role may grant as a whole: saved with no children, the module answers for its own tabs
 * from its own flags, the leaf rule resolvePermission's resolveModulePermission applies.
 * Without it a childless Manage Orders (recruiting_manager, ops_manager, lead_manager,
 * msp_business_analyst, msp_accounts_manager, msp_owner) answered (manage_orders, service_orders)
 * from whatever `service_orders` node the cross-module search hit first: none, so the route denied
 * a role meant to manage orders, or msp_owner's Companies > Orders > Service Orders, so unticking
 * Manage Orders left it open (STFFLG-4269, now that its nav item asks the route's question).
 * Deliberately not every module: `users_roles` is a childless leaf whose deeper keys
 * (`roles`, `employee_metrics`) are answered by the alias and cross-module search below.
 */
const WHOLE_MODULE_KEYS = new Set(['manage_orders'])

/**
 * Helper to find a key in submodules (reduces nesting depth).
 * Searches every module's submodule tree, at any depth.
 */
function findKeyInSubmodules(
  modulePermissions: ModulePermission[],
  targetKey: string,
): boolean | undefined {
  for (const mod of modulePermissions) {
    const result = findKeyInModuleSubmodules(mod.submodules, targetKey)
    if (result !== undefined) return result
  }
  return undefined
}

/**
 * Searches a single module's submodule tree, at any depth, for a target key.
 */
function findKeyInModuleSubmodules(
  submodules: ModulePermission[] | undefined,
  targetKey: string,
): boolean | undefined {
  if (!submodules || submodules.length === 0) return undefined
  const direct = submodules.find((s) => s.key === targetKey)
  if (direct) return direct.canView
  // Recurse. This was hardcoded to look exactly one level below `submodules`, which sufficed while
  // the deepest key sat at manage_x > module > submodule. `employee_metrics` lives one level
  // further down: manage_agencies > agency_users_roles > users_list > employee_metrics.
  //
  // The failure was FAIL-OPEN, not fail-closed, which is why nothing caught it. A key the search
  // could not reach fell through to the alias path below, where `users_roles` resolves to the first
  // alias node found (`company_users_roles` on real trees, else `agency_users_roles`) — a node that IS
  // viewable — and hasModulePermission returned true. So a
  // route keyed on a too-deep permission was granted to anyone who could see Internal Employees,
  // with the permission's own flag never consulted. It looked enforced and was not.
  for (const sub of submodules) {
    const nested = findKeyInModuleSubmodules(sub.submodules, targetKey)
    if (nested !== undefined) return nested
  }
  return undefined
}

export interface ModulePermissionOptions {
  /**
   * Answer a submoduleKey check ONLY from a node whose key IS that submoduleKey — under the module,
   * or anywhere in the tree via the cross-module search — and DENY when no such node exists. No
   * alias of either key is consulted, and the module's own flags never stand in for the
   * submodule's. Without a submoduleKey it changes nothing.
   *
   * The default is lenient: a submoduleKey found nowhere falls through to the MODULE key's aliases
   * and is answered by the first alias node that turns up (see the fall-through below). Existing
   * routes rely on that (STFFLG-3196), so it stays the default. But it makes a key the viewer's tree
   * does not carry fail OPEN: for ('users_roles', 'employee_metrics') on a tree without the node the
   * answer comes from `company_users_roles` — the client's Users & Roles tab — and
   * `employee_metrics` is never read. Company trees never carry the node, nor does an Agency role
   * with no Employees tab. Since PO item 6 every other Agency tree does, and that node is the only
   * thing that admits it. It never carries the node's Financials child, which the Agency catalogue
   * omits. Opt in per route with RouteConfig.strictSubmodule.
   */
  strict?: boolean
}

/**
 * Checks if user has permission for a moduleKey
 * Looks up the moduleKey in the user's modulePermissions and checks canView
 * Also searches submodules across all modules when submoduleKey is provided
 * Supports module key aliases for role-specific permission mappings
 *
 * Returns `true`/`false` when the requested node was found in the tree (an
 * explicit grant or deny), and `undefined` when it was not found at all.
 * Callers must distinguish the two: an absent node carries no opinion, which is
 * how admin-category roles keep access to routes whose permission node has not
 * been seeded yet. This mirrors AuthorizerGuard's modulePermissions semantics
 * on the backend (steps 3 and 4 of auth.guard.ts).
 * Pass {strict: true} to deny a submoduleKey that no node carries (see ModulePermissionOptions)
 */
export const hasModulePermission = (
  modulePermissions: ModulePermission[] | undefined,
  moduleKey: string,
  submoduleKey?: string,
  options: ModulePermissionOptions = {},
): boolean | undefined => {
  // Handle null, undefined, or empty array - allow access (API will validate)
  // Strict mode included: with no tree at all there is nothing to be strict against.
  if (!modulePermissions || !Array.isArray(modulePermissions) || modulePermissions.length === 0) {
    // If no permissions data, allow access (will be restricted by API)
    return true
  }

  // First, try to find the module directly
  // Use case-insensitive comparison as a fallback for key mismatches
  const module =
    modulePermissions.find((m) => m.key === moduleKey) ||
    modulePermissions.find((m) => m.key?.toLowerCase() === moduleKey?.toLowerCase())

  // Strict: only a node carrying the exact key may answer, and finding none is a denial rather than
  // a cue to ask the parent. Returns before every alias step below, so none of them can run.
  if (options.strict && submoduleKey) {
    const underModule = findKeyInModuleSubmodules(module?.submodules, submoduleKey)
    if (underModule !== undefined) return underModule
    return findKeyInSubmodules(modulePermissions, submoduleKey) ?? false
  }

  if (module) {
    // If checking a submodule under this module (supports 3-level nesting)
    if (submoduleKey) {
      if (!module.submodules?.length && WHOLE_MODULE_KEYS.has(module.key)) return module.canView
      const result = findKeyInModuleSubmodules(module.submodules, submoduleKey)
      if (result !== undefined) return result

      const subAliases = MODULE_KEY_ALIASES[submoduleKey]
      const aliasResult = subAliases
        ?.map((alias) => findKeyInModuleSubmodules(module.submodules, alias))
        .find((r) => r !== undefined)
      if (aliasResult !== undefined) return aliasResult
      // Submodule not found under the direct module — fall through to check
      // module aliases (e.g. agency_users_roles may have the 'roles' submodule
      // even when the top-level users_roles node doesn't).
    } else {
      if (module.canView) {
        return true
      }
      // Top-level canView is false — still allow if any submodule has canView: true
      if (module.submodules?.some((s) => s.canView)) {
        return true
      }
      return false
    }
  }

  // Module not found directly - if we have a submoduleKey, search across all modules' submodules
  // This handles cases like general_admin where 'roles_permissions' is under 'global' module
  if (submoduleKey) {
    const result = findKeyInSubmodules(modulePermissions, submoduleKey)
    if (result !== undefined) return result
  }

  // Also check if moduleKey itself exists as a submodule anywhere
  // This handles cases where a route's moduleKey is actually a submodule
  for (const mod of modulePermissions) {
    if (mod.submodules && mod.submodules.length > 0) {
      const submodule = mod.submodules.find((s) => s.key === moduleKey)
      if (submodule) {
        return submodule.canView
      }
    }
  }

  // Check for module key aliases (role-specific permission mappings)
  // This handles cases like 'users_roles' -> 'company_users_roles' for company admins
  const aliases = MODULE_KEY_ALIASES[moduleKey]
  if (aliases && aliases.length > 0) {
    for (const aliasKey of aliases) {
      // Check if alias exists as a direct module
      const aliasModule = modulePermissions.find((m) => m.key === aliasKey)
      if (aliasModule) {
        if (aliasModule.canView) {
          return true
        }
        if (aliasModule.submodules?.some((s) => s.canView)) {
          return true
        }
        return false
      }

      // Check if alias exists as a submodule (uses helper to reduce nesting)
      const submoduleResult = findKeyInSubmodules(modulePermissions, aliasKey)
      if (submoduleResult !== undefined) {
        return submoduleResult
      }
    }
  }

  // Module/submodule not in the permissions list at all - no opinion.
  return undefined
}

// ============================================================================
// LEGACY KEY FALLBACK (Finance RBAC)
// ============================================================================

/**
 * A module that used to be gated on another key, read from that key while the user's permission
 * tree has no node for it at all. Permissions are persisted (redux-persist) and only rebuilt at
 * sign-in, so a session opened before the backend added `finance` carries no finance node: without
 * the fallback every platform admin would lose the Finance tab until they signed in again. The
 * backend migration grants `finance` exactly where `payroll` is visible, so the fallback answers
 * as the next sign-in will. A present node (granted or unticked) always wins.
 */
export const LEGACY_MODULE_KEY_FALLBACK: Record<string, string> = {
  finance: 'payroll',
}

const treeHasKey = (nodes: ModulePermission[] | undefined, key: string): boolean =>
  !!nodes?.some((n) => n.key === key || treeHasKey(n.submodules, key))

/** The key to check: `moduleKey`, or its legacy key when the tree carries no `moduleKey` node. */
export const resolveLegacyModuleKey = (
  modulePermissions: ModulePermission[] | undefined,
  moduleKey: string,
): string => {
  const legacy = LEGACY_MODULE_KEY_FALLBACK[moduleKey]
  if (!legacy || !modulePermissions?.length || treeHasKey(modulePermissions, moduleKey)) {
    return moduleKey
  }
  return legacy
}
